import { useMemo } from 'react';

import dayjs from 'dayjs';

import { DateRange, DateTime, TimeRange } from '@cast/shared-types';

import { useIsReliabilityMetricsAvailableInBeta } from 'hooks/permissions/useIsReliabilityMetricsAvailableInBeta';
import { useWorkloadReliabilityMetricsQuery } from 'hooks/queries/cost/useWorkloadReliabilityMetricsQuery';
import { DateRangeFilter } from '@cast/shared-date-ui';
import { granularityToSeconds } from 'utils/costReport';

import { RELIABILITY_METRICS } from '../_constants/reliabilityMetrics';
import { RELIABILITY_REFETCH_INTERVAL } from '../_constants/reliabilityRefetchInterval';
import {
  buildReliabilityChartData,
  hasReliabilityData,
} from '../_utils/buildReliabilityChartData';
import { convertLatencyToMs } from '../_utils/convertLatencyToMs';
import { getReliabilityGranularity } from '../_utils/getReliabilityGranularity';

import { useCluster } from '@cast/shared-cluster-context-data-access';

type UseReliabilityDataParams = {
  namespace?: string;
  workloadType?: string;
  workloadName?: string;
  granularDateRange: TimeRange<DateTime>;
  timezone: string;
  currentDateRange: DateRange;
  currentDateRangeFilter?: DateRangeFilter;
  includeTable?: boolean;
  includePreviousPeriod?: boolean;
  hasReliabilityMetrics?: boolean;
};

export const useReliabilityData = ({
  namespace,
  workloadType,
  workloadName,
  granularDateRange,
  timezone,
  currentDateRange,
  currentDateRangeFilter,
  includeTable = true,
  includePreviousPeriod = true,
  hasReliabilityMetrics = true,
}: UseReliabilityDataParams) => {
  const { cluster } = useCluster();
  const isReliabilityEnabled = useIsReliabilityMetricsAvailableInBeta();
  const granularity = useMemo(
    () => getReliabilityGranularity(currentDateRange),
    [currentDateRange]
  );
  const refetchInterval = currentDateRangeFilter
    ? RELIABILITY_REFETCH_INTERVAL[currentDateRangeFilter]
    : undefined;

  const tableDateRange = useMemo(() => {
    const to = dayjs(granularDateRange.to)
      .tz(timezone)
      .startOf('day')
      .add(1, 'day');

    return {
      from: to.subtract(30, 'day'),
      to,
    };
  }, [granularDateRange.to, timezone]);

  const previousPeriodDateRange = useMemo(() => {
    const from = dayjs(granularDateRange.from);
    const to = dayjs(granularDateRange.to);
    const periodDurationMs = to.diff(from);

    return {
      from: from.subtract(periodDurationMs, 'millisecond'),
      to: from,
    };
  }, [granularDateRange.from, granularDateRange.to]);

  const enabled =
    isReliabilityEnabled &&
    hasReliabilityMetrics &&
    !!namespace &&
    !!workloadType &&
    !!workloadName;

  const commonParams = {
    clusterId: cluster.id,
    namespace: namespace!,
    workloadType: workloadType!,
    workloadName: workloadName!,
    timezone,
  };

  const { data, isLoading, isFetching, error, refetch } =
    useWorkloadReliabilityMetricsQuery(
      {
        ...commonParams,
        startTime: granularDateRange.from.toISOString(),
        endTime: granularDateRange.to.toISOString(),
        stepSeconds: granularityToSeconds(granularity),
      },
      { select: convertLatencyToMs, enabled, refetchInterval }
    );

  const { data: dailyTableData, isLoading: isDailyTableLoading } =
    useWorkloadReliabilityMetricsQuery(
      {
        ...commonParams,
        startTime: tableDateRange.from.toISOString(),
        endTime: tableDateRange.to.toISOString(),
        stepSeconds: granularityToSeconds({ step: 1, unit: 'day' }),
      },
      { select: convertLatencyToMs, enabled: enabled && includeTable }
    );

  const { data: previousPeriodData } = useWorkloadReliabilityMetricsQuery(
    {
      ...commonParams,
      startTime: previousPeriodDateRange.from.toISOString(),
      endTime: previousPeriodDateRange.to.toISOString(),
      stepSeconds: granularityToSeconds(granularity),
    },
    { select: convertLatencyToMs, enabled: enabled && includePreviousPeriod }
  );

  const chartData = useMemo(
    () => buildReliabilityChartData(data, granularDateRange, granularity),
    [data, granularDateRange, granularity]
  );

  const trafficMetrics = RELIABILITY_METRICS.filter(
    ({ chartGroup }) => chartGroup === 'traffic'
  );
  const latencyMetrics = RELIABILITY_METRICS.filter(
    ({ chartGroup }) => chartGroup === 'latency'
  );

  const showNoData =
    !isLoading &&
    !isDailyTableLoading &&
    !hasReliabilityData(data) &&
    !hasReliabilityData(dailyTableData);

  return {
    data,
    previousPeriodData,
    dailyTableData,
    chartData,
    trafficMetrics,
    latencyMetrics,
    isLoading,
    isFetching,
    isDailyTableLoading,
    showNoData,
    error,
    refetch,
  };
};
